import type { RateDTO, ZoneDTO } from "../api/client";
import { formatPriceLak } from "@/shared/lib/format-price";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, Trash2Icon } from "lucide-react";

function zoneLabel(zoneId: string, zones: ZoneDTO[]): string {
  const z = zones.find((x) => x.id === zoneId);
  return z ? z.code : zoneId;
}

type RatesTableProps = {
  canManage: boolean;
  zones: ZoneDTO[];
  isLoading: boolean;
  data: RateDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: RateDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function RatesTable({
  canManage,
  zones,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: RatesTableProps) {
  const columns: TanstackReactTable.ColumnDef<RateDTO>[] = [
    {
      id: "zoneId",
      header: "ໂຊນ",
      cell: ({ row }) => zoneLabel(row.original.zoneId, zones),
      size: 100,
    },
    {
      id: "weightMinGrams",
      header: "ນ້ຳໜັກຕ່ຳ (g)",
      cell: ({ row }) => row.original.weightMinGrams,
      size: 100,
    },
    {
      id: "weightMaxGrams",
      header: "ນ້ຳໜັກສູງ (g)",
      cell: ({ row }) => row.original.weightMaxGrams,
      size: 100,
    },
    {
      id: "amount",
      header: "ຄ່າຂົນສົ່ງ",
      cell: ({ row }) => formatPriceLak(row.original.amount, { suffix: true }),
      size: 100,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const id = row.original.id;
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <Trash2Icon className="h-4 w-4" />,
            onClick: () => onDelete(id),
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 120,
    },
  ];

  return (
    <div data-tourid="shipping-rates-table">
      <DataTable<RateDTO, unknown>
        noDataMessage="ບໍ່ພົບອັດຕາຄ່າສົ່ງ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id as string, !!s[0]?.desc);
        }}
      />
    </div>
  );
}
